import { useCallback, useEffect, useRef, useState } from "react";
import { DownloadIcon, PauseIcon, PlayIcon, Volume2Icon, VolumeXIcon } from "lucide-react";
import { cn } from "@util/tailwind";

interface AudioRecordingPlayerProps {
    src: string;
    /** Bubble the loaded duration up (mirrors InterviewPlayer's setVideoDuration). */
    onDurationChange?: (seconds: number) => void;
    /** Show the download button. Off by default — callers opt in. */
    showDownload?: boolean;
    className?: string;
}

function formatTime(seconds: number): string {
    if (!Number.isFinite(seconds) || seconds < 0) return "0:00";
    const m = Math.floor(seconds / 60);
    const s = Math.floor(seconds % 60);
    return `${m}:${String(s).padStart(2, "0")}`;
}

/**
 * Compact audio player for an audio-only interview recording (mp3 at `s3_link`).
 * Styled after the training RecordingPlayer — blue circular play/pause, a
 * scrubbable progress bar, time, mute, and an optional download — but deliberately
 * WITHOUT the timeline feedback dots and the speed selector.
 */
export const AudioRecordingPlayer = ({ src, onDurationChange, showDownload = false, className }: AudioRecordingPlayerProps) => {
    const audioRef = useRef<HTMLAudioElement>(null);
    const trackRef = useRef<HTMLDivElement>(null);
    const [isPlaying, setIsPlaying] = useState(false);
    const [isMuted, setIsMuted] = useState(false);
    const [isDragging, setIsDragging] = useState(false);
    const [currentTime, setCurrentTime] = useState(0);
    const [duration, setDuration] = useState(0);

    useEffect(() => {
        const audio = audioRef.current;
        if (!audio) return;
        const onTime = () => setCurrentTime(audio.currentTime);
        const onDuration = () => {
            if (Number.isFinite(audio.duration) && audio.duration > 0) {
                setDuration(audio.duration);
                onDurationChange?.(audio.duration);
            }
        };
        const onPlay = () => setIsPlaying(true);
        const onPause = () => setIsPlaying(false);
        const onEnded = () => setIsPlaying(false);
        audio.addEventListener("timeupdate", onTime);
        audio.addEventListener("loadedmetadata", onDuration);
        audio.addEventListener("durationchange", onDuration);
        audio.addEventListener("play", onPlay);
        audio.addEventListener("pause", onPause);
        audio.addEventListener("ended", onEnded);
        return () => {
            audio.removeEventListener("timeupdate", onTime);
            audio.removeEventListener("loadedmetadata", onDuration);
            audio.removeEventListener("durationchange", onDuration);
            audio.removeEventListener("play", onPlay);
            audio.removeEventListener("pause", onPause);
            audio.removeEventListener("ended", onEnded);
        };
    }, [onDurationChange]);

    const togglePlay = useCallback(() => {
        const audio = audioRef.current;
        if (!audio) return;
        if (audio.paused) {
            audio.play().catch(() => {});
        } else {
            audio.pause();
        }
    }, []);

    const toggleMute = useCallback(() => {
        const audio = audioRef.current;
        if (!audio) return;
        audio.muted = !audio.muted;
        setIsMuted(audio.muted);
    }, []);

    const seekToPosition = useCallback((clientX: number) => {
        const track = trackRef.current;
        const audio = audioRef.current;
        if (!track || !audio) return;
        const dur = Number.isFinite(audio.duration) && audio.duration > 0 ? audio.duration : duration;
        if (!dur) return;
        const rect = track.getBoundingClientRect();
        const ratio = Math.max(0, Math.min(1, (clientX - rect.left) / rect.width));
        // Clamp just shy of the end — some browsers treat currentTime === duration
        // as "past end" and snap back to 0.
        const safe = Math.min(ratio * dur, Math.max(0, dur - 0.05));
        audio.currentTime = safe;
        setCurrentTime(safe);
    }, [duration]);

    const handleTrackMouseDown = useCallback((e: React.MouseEvent) => {
        setIsDragging(true);
        seekToPosition(e.clientX);
    }, [seekToPosition]);

    useEffect(() => {
        if (!isDragging) return;
        const onMove = (e: MouseEvent) => seekToPosition(e.clientX);
        const onUp = () => setIsDragging(false);
        window.addEventListener("mousemove", onMove);
        window.addEventListener("mouseup", onUp);
        return () => {
            window.removeEventListener("mousemove", onMove);
            window.removeEventListener("mouseup", onUp);
        };
    }, [isDragging, seekToPosition]);

    const progress = duration > 0 ? (currentTime / duration) * 100 : 0;

    return (
        <div className={cn("flex items-center gap-3 rounded-xl border border-gray-200 dark:border-gray-700 bg-white dark:bg-gray-900 px-4 py-3", className)}>
            <audio ref={audioRef} src={src} preload="metadata" onContextMenu={(e) => e.preventDefault()} />

            {/* Play / Pause */}
            <button
                type="button"
                onClick={togglePlay}
                className="flex h-9 w-9 shrink-0 items-center justify-center rounded-full bg-blue-600 text-white hover:bg-blue-700 transition-colors"
                aria-label={isPlaying ? "Pause" : "Play"}
            >
                {isPlaying ? <PauseIcon className="h-4 w-4" /> : <PlayIcon className="h-4 w-4 ml-0.5" />}
            </button>

            {/* Scrub bar (no timeline dots) */}
            <div
                ref={trackRef}
                className="relative h-2 flex-1 cursor-pointer rounded-full bg-gray-200 dark:bg-gray-700"
                onMouseDown={handleTrackMouseDown}
            >
                <div
                    className="absolute inset-y-0 left-0 rounded-full bg-blue-500"
                    style={{ width: `${progress}%` }}
                />
                <div
                    className={cn(
                        "absolute top-1/2 -translate-y-1/2 -translate-x-1/2 h-4 w-4 rounded-full border-2 border-blue-600 bg-white shadow-sm transition-transform",
                        isDragging ? "scale-110 cursor-grabbing" : "hover:scale-105 cursor-grab",
                    )}
                    style={{ left: `${progress}%` }}
                />
            </div>

            {/* Time */}
            <span className="shrink-0 text-xs tabular-nums text-gray-500 dark:text-gray-400">
                {formatTime(currentTime)} / {formatTime(duration)}
            </span>

            {/* Mute */}
            <button
                type="button"
                onClick={toggleMute}
                className="flex h-7 w-7 items-center justify-center rounded text-gray-500 hover:text-gray-700 dark:text-gray-400 dark:hover:text-gray-200 transition-colors"
                aria-label={isMuted ? "Unmute" : "Mute"}
            >
                {isMuted ? <VolumeXIcon className="h-4 w-4" /> : <Volume2Icon className="h-4 w-4" />}
            </button>

            {/* Download (opt-in) */}
            {showDownload && (
                <a
                    href={src}
                    download
                    className="flex h-7 w-7 items-center justify-center rounded text-gray-500 hover:text-gray-700 dark:text-gray-400 dark:hover:text-gray-200 transition-colors"
                    aria-label="Download recording"
                >
                    <DownloadIcon className="h-4 w-4" />
                </a>
            )}
        </div>
    );
};
